import { create } from "zustand";

export type TSlide = {
  id: string;
  title: string;
  outline: string;
  order: number;
  updatedAt?: string;
};

export type TPresentationStatus = "generating" | "completed" | "failed" | null;
export type TGammaStatus = "generating" | "completed" | "failed" | null;

export type TPresentation = {
  id: number;
  projectId: number;
  status: TPresentationStatus;
  gammaStatus: TGammaStatus;
  gammaWebUrl: string | null;
  gammaPptxUrl: string | null;
  gammaPdfUrl: string | null;
  slidesGeneratedAt: string | null;
  slidesUpdatedAt: string;
  gammaGeneratedAt: string;
  allowRegeneration: boolean;
  slides: TSlide[];
};

type TPresentationState = {
  presentation: TPresentation | null;
  localPresentation: TPresentation | null;
  isLoading: boolean;
  isDirty: boolean;
  isGammaGenerating: boolean;
  isSlidesGenerating: boolean;
  isSaving: boolean;
  isSlidesRegenerating: boolean;
  pendingSlideIds: Set<string>;
  slidesUpdatedFromChat: boolean;

  setPresentation: (presentation: TPresentation) => void;
  setIsGammaGenerating: (isGammaGenerating: boolean) => void;
  setIsSlidesGenerating: (isSlidesGenerating: boolean) => void;
  setIsSaving: (isSaving: boolean) => void;
  setIsSlidesRegenerating: (isSlidesRegenerating: boolean) => void;
  setLocalPresentation: (localPresentation: TPresentation) => void;
  setIsLoading: (isLoading: boolean) => void;
  updateSlide: (id: string, updates: Partial<TSlide>) => void;
  deleteSlide: (id: string) => void;
  reorderSlides: (newOrder: TSlide[]) => void;
  addSlide: (afterIndex?: number) => string;
  markDirty: () => void;
  markClean: () => void;
  reset: () => void;
  hasPendingEmptySlides: () => boolean;
  setSlidesUpdatedFromChat: (value: boolean) => void;
};

const initialState = {
  presentation: null,
  localPresentation: null,
  isLoading: false,
  isDirty: false,
  isGammaGenerating: false,
  isSlidesGenerating: false,
  isSaving: false,
  isSlidesRegenerating: false,
  pendingSlideIds: new Set<string>(),
  slidesUpdatedFromChat: false
};

export const usePresentationStore = create<TPresentationState>((set, get) => ({
  ...initialState,

  setPresentation: (presentation) => set({ presentation }),
  setLocalPresentation: (localPresentation) => set({ localPresentation, pendingSlideIds: new Set() }),
  setIsLoading: (isLoading) => set({ isLoading }),
  updateSlide: (id, updates) =>
    set((state) => {
      const updatedSlides =
        state.localPresentation?.slides.map((slide) => (slide.id === id ? { ...slide, ...updates } : slide)) || [];

      const updatedSlide = updatedSlides.find((s) => s.id === id);
      const hasContent = updatedSlide && (updatedSlide.title?.trim() || updatedSlide.outline?.trim());

      let newPendingIds = state.pendingSlideIds;
      let shouldMarkDirty = state.isDirty;

      if (state.pendingSlideIds.has(id)) {
        if (hasContent) {
          newPendingIds = new Set(state.pendingSlideIds);
          newPendingIds.delete(id);
          shouldMarkDirty = true;
        }
      } else {
        shouldMarkDirty = true;
      }

      return {
        localPresentation: state.localPresentation
          ? { ...state.localPresentation, slides: updatedSlides, allowRegeneration: true }
          : null,
        pendingSlideIds: newPendingIds,
        isDirty: shouldMarkDirty
      };
    }),
  deleteSlide: (id) =>
    set((state) => {
      const newPendingIds = new Set(state.pendingSlideIds);
      newPendingIds.delete(id);

      return {
        localPresentation: state.localPresentation
          ? {
              ...state.localPresentation,
              slides: state.localPresentation.slides.filter((slide) => slide.id !== id),
              allowRegeneration: true
            }
          : null,
        pendingSlideIds: newPendingIds,
        isDirty: true
      };
    }),
  reorderSlides: (newOrder) =>
    set((state) => {
      const reorderedSlides = newOrder.map((slide, index) => ({
        ...slide,
        order: index + 1
      }));
      return {
        localPresentation: state.localPresentation
          ? { ...state.localPresentation, slides: reorderedSlides, allowRegeneration: true }
          : null,
        isDirty: true
      };
    }),
  addSlide: (afterIndex?: number) => {
    const newSlideId = `slide-${Date.now()}`;
    set((state) => {
      const slides = state.localPresentation?.slides || [];
      const insertIndex = afterIndex !== undefined ? afterIndex + 1 : slides.length;
      
      const newSlide: TSlide = {
        id: newSlideId,
        title: "",
        outline: "",
        order: insertIndex + 1
      };

      const updatedSlides = [...slides];
      updatedSlides.splice(insertIndex, 0, newSlide);
      
      const reorderedSlides = updatedSlides.map((slide, index) => ({
        ...slide,
        order: index + 1
      }));

      const newPendingIds = new Set(state.pendingSlideIds);
      newPendingIds.add(newSlideId);

      return {
        localPresentation: state.localPresentation
          ? {
              ...state.localPresentation,
              slides: reorderedSlides,
              allowRegeneration: true
            }
          : null,
        pendingSlideIds: newPendingIds
      };
    });
    return newSlideId;
  },

  markDirty: () => set({ isDirty: true }),
  markClean: () => set({ isDirty: false, pendingSlideIds: new Set() }),
  reset: () => set(initialState),
  setIsGammaGenerating: (isGammaGenerating) => set({ isGammaGenerating }),
  setIsSlidesGenerating: (isSlidesGenerating) => set({ isSlidesGenerating }),
  setIsSaving: (isSaving) => set({ isSaving }),
  setIsSlidesRegenerating: (isSlidesRegenerating) => set({ isSlidesRegenerating }),
  setSlidesUpdatedFromChat: (value: boolean) => set({ slidesUpdatedFromChat: value }),
  hasPendingEmptySlides: () => {
    const state = get();
    if (state.pendingSlideIds.size === 0) return false;

    const slides = state.localPresentation?.slides || [];
    for (const slideId of state.pendingSlideIds) {
      const slide = slides.find((s) => s.id === slideId);
      if (slide && !slide.title?.trim() && !slide.outline?.trim()) {
        return true;
      }
    }
    return false;
  }
}));
